import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet';

const SectorPerformance = () => {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'changesPercentage',
    direction: 'desc'
  });

  const title = "Sektör Performansları | Smart Portfoy";
  const description = "Global piyasalarda sektörlerin performanslarını anlık olarak takip edin. Sektörel değişimler ve güncel veriler.";

  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const apiKey = process.env.REACT_APP_FMP_API_KEY;
        if (!apiKey) {
          throw new Error('API key is not configured');
        }

        const response = await fetch(
          `https://financialmodelingprep.com/api/v3/sector-performance?apikey=${apiKey}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const data = await response.json();
        console.log('API Response:', data);
        setSectors(data);
      } catch (error) {
        console.error('Error fetching sectors:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSectors();
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key
          ? prev.direction === 'asc'
            ? 'desc'
            : 'asc'
          : 'desc',
    }));
  };

  const getSortedData = () => {
    if (!sortConfig.key || !sectors.length) return sectors;

    return [...sectors].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle percentage strings
      if (typeof aValue === 'string' && aValue.includes('%')) {
        aValue = parseFloat(aValue.replace('%', ''));
        bValue = parseFloat(bValue.replace('%', ''));
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <div className="w-4" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center">
          <p className="dark:text-gray-300">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-center text-red-600 dark:text-red-400">
          <p>Hata: {error}</p>
        </div>
      </div>
    );
  }

  const sortedData = getSortedData();
  const midPoint = Math.ceil(sortedData.length / 2);
  const leftColumn = sortedData.slice(0, midPoint);
  const rightColumn = sortedData.slice(midPoint);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href="https://smart-portfoy.com/market/sectors" />
        <meta name="keywords" content="borsa, sektör performansı, piyasa analizi, sektörel analiz, yatırım stratejisi" />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-emerald-900 to-blue-900 py-24">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-blue-400">
              Sektör Performansları
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
              Global piyasalarda sektörlerin performanslarını takip edin. Güncel değişimler ve sektörel analizlerle yatırım stratejinizi belirleyin.
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-32">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
            src="/images/market/market-banner.jpg" 
            alt="Market Sectors Banner"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sektör</span>
                  <div 
                    className="flex items-center space-x-1 cursor-pointer"
                    onClick={() => handleSort('changesPercentage')}
                  >
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Değişim</span>
                    {getSortIcon('changesPercentage')}
                  </div>
                </div>
                {leftColumn.map((sector, index) => {
                  const changeValue = parseFloat(sector.changesPercentage.replace('%', ''));
                  return (
                    <div 
                      key={index}
                      className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {sector.sector}
                      </span>
                      <span className={`text-sm ${
                        changeValue >= 0 
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {changeValue >= 0 ? '+' : ''}
                        {sector.changesPercentage}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Column */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700 flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Sektör</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Değişim</span>
                </div>
                {rightColumn.map((sector, index) => {
                  const changeValue = parseFloat(sector.changesPercentage.replace('%', ''));
                  return (
                    <div 
                      key={index}
                      className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {sector.sector}
                      </span>
                      <span className={`text-sm ${
                        changeValue >= 0 
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {changeValue >= 0 ? '+' : ''}
                        {sector.changesPercentage}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SectorPerformance;